import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Page from "../../components/Page";
import {
  IconDashboard,
  IconArrowUpRight,
  IconUsers,
  IconArrowDownLeft,
  IconFlame,
  IconCheck,
  IconFileText,
  IconChartBar,
  IconRotate,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import {
  useItemIssueDashboard,
  useEligibleItems,
  useActiveShifts,
  useCustodyRegister,
  useMovements,
  useItemIssueReports,
} from "../../controllers/item_issue.controller";
import { useUsers } from "../../controllers/users.controller";

import DashboardView from "./DashboardView";
import IssueItemsView from "./IssueItemsView";
import CustodyRegisterView from "./CustodyRegisterView";
import ReturnItemsView from "./ReturnItemsView";
import RecordConsumptionView from "./RecordConsumptionView";
import ReconciliationView from "./ReconciliationView";
import MovementHistoryView from "./MovementHistoryView";
import ReportsView from "./ReportsView";
import UnifiedSettleReconcileModal from "./UnifiedSettleReconcileModal";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: IconDashboard },
  { id: "issue", label: "Issue Items", icon: IconArrowUpRight },
  { id: "custody", label: "Custody Register", icon: IconUsers },
  { id: "return", label: "Return Items", icon: IconArrowDownLeft },
  { id: "consumption", label: "Non-Sale Usage", icon: IconFlame },
  { id: "reconciliation", label: "Audit & Reconcile", icon: IconCheck },
  { id: "movements", label: "Movement Ledger", icon: IconFileText },
  { id: "reports", label: "Reports", icon: IconChartBar },
];

export default function ItemIssuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "dashboard";

  const setTab = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  // Pre-fill state passed between Custody Register and action views
  const [prefilledRow, setPrefilledRow] = useState(null);
  // Unified Settle & Reconcile Modal state
  const [unifiedSettleRow, setUnifiedSettleRow] = useState(null);

  // SWR hooks
  const {
    stats,
    isLoading: isDashboardLoading,
    mutate: mutateDashboard,
  } = useItemIssueDashboard();

  const {
    items: eligibleItems,
    isLoading: isItemsLoading,
    mutate: mutateEligibleItems,
  } = useEligibleItems();

  const { shifts, isLoading: isShiftsLoading } = useActiveShifts();
  const { data: usersData } = useUsers();
  const users = Array.isArray(usersData) ? usersData : usersData?.users || [];

  const {
    register,
    isLoading: isRegisterLoading,
    mutate: mutateRegister,
  } = useCustodyRegister({ status: "all" });

  // Movements filter & pagination state
  const [movementFilters, setMovementFilters] = useState({
    search: "",
    movement_type: "all",
    from: "",
    to: "",
    page: 1,
    limit: 20,
  });

  const {
    movements,
    total: totalMovements,
    page: movementPage,
    isLoading: isMovementsLoading,
    mutate: mutateMovements,
  } = useMovements(movementFilters);

  // Reports filter state
  const [reportFilters, setReportFilters] = useState({
    from: "",
    to: "",
    type: "product",
  });

  const {
    reports,
    summary: reportSummary,
    isLoading: isReportsLoading,
    mutate: mutateReports,
  } = useItemIssueReports(reportFilters);

  // Refresh all caches on success
  const handleMutationSuccess = () => {
    mutateDashboard();
    mutateEligibleItems();
    mutateRegister();
    mutateMovements();
    mutateReports();
    setPrefilledRow(null);
    setTab("custody");
  };

  // Row action handlers from Custody Register
  const handleActionReturn = (row) => {
    setPrefilledRow(row);
    setTab("return");
  };

  const handleActionConsume = (row) => {
    setPrefilledRow(row);
    setTab("consumption");
  };

  const handleActionReconcile = (row) => {
    setPrefilledRow(row);
    setTab("reconciliation");
  };

  return (
    <Page className="px-4 py-3 md:px-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-base-content tracking-tight">
            Item Issue & Return Management
          </h1>
          <p className="text-xs text-base-content/60 mt-1">
            General stock custody, unit conversions, waiter sales tracking, returns, and physical audit reconciliation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              mutateDashboard();
              mutateEligibleItems();
              mutateRegister();
              mutateMovements();
              mutateReports();
            }}
            className="btn btn-sm btn-ghost gap-1 text-xs"
            title="Refresh All Data"
          >
            <IconRotate size={16} stroke={iconStroke} />
            Refresh
          </button>
        </div>
      </div>

      {/* Navigation Tabs Bar */}
      <div className="bg-base-100 p-1.5 rounded-2xl border border-base-200 shadow-sm overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  isActive
                    ? "bg-primary text-primary-content shadow-sm"
                    : "text-base-content/70 hover:bg-base-200 hover:text-base-content"
                }`}
              >
                <Icon size={16} stroke={iconStroke} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Views Content */}
      <div className="mt-4">
        {currentTab === "dashboard" && (
          <DashboardView
            stats={stats}
            isLoading={isDashboardLoading}
            onSelectTab={setTab}
          />
        )}

        {currentTab === "issue" && (
          <IssueItemsView
            eligibleItems={eligibleItems}
            shifts={shifts}
            users={users}
            onIssueSuccess={handleMutationSuccess}
          />
        )}

        {currentTab === "custody" && (
          <CustodyRegisterView
            register={register}
            isLoading={isRegisterLoading}
            onActionReturn={handleActionReturn}
            onActionConsume={handleActionConsume}
            onActionReconcile={handleActionReconcile}
            onOpenUnifiedSettle={(row) => setUnifiedSettleRow(row)}
          />
        )}

        {currentTab === "return" && (
          <ReturnItemsView
            register={register}
            eligibleItems={eligibleItems}
            users={users}
            prefilledRow={prefilledRow}
            onReturnSuccess={handleMutationSuccess}
          />
        )}

        {currentTab === "consumption" && (
          <RecordConsumptionView
            register={register}
            eligibleItems={eligibleItems}
            users={users}
            prefilledRow={prefilledRow}
            onConsumptionSuccess={handleMutationSuccess}
          />
        )}

        {currentTab === "reconciliation" && (
          <ReconciliationView
            register={register}
            eligibleItems={eligibleItems}
            shifts={shifts}
            users={users}
            prefilledRow={prefilledRow}
            onReconciliationSuccess={handleMutationSuccess}
          />
        )}

        {currentTab === "movements" && (
          <MovementHistoryView
            movements={movements}
            total={totalMovements}
            page={movementPage}
            limit={movementFilters.limit}
            isLoading={isMovementsLoading}
            filters={movementFilters}
            onFilterChange={setMovementFilters}
            onPageChange={(p) => setMovementFilters({ ...movementFilters, page: p })}
          />
        )}

        {currentTab === "reports" && (
          <ReportsView
            reports={reports}
            summary={reportSummary}
            isLoading={isReportsLoading}
            filters={reportFilters}
            onFilterChange={setReportFilters}
          />
        )}
      </div>

      {/* Unified Settle, Return & Reconcile Modal */}
      <UnifiedSettleReconcileModal
        isOpen={!!unifiedSettleRow}
        onClose={() => setUnifiedSettleRow(null)}
        custodyRecord={unifiedSettleRow}
        eligibleItems={eligibleItems}
        onSuccess={handleMutationSuccess}
      />
    </Page>
  );
}
